import { DateTime } from 'luxon';
import { action, computed, makeAutoObservable, makeObservable, observable } from 'mobx';
import { ResourceStatus } from '../../api/Api';
import { ContactApiResponse, EntityApi, EntityApiResponse, NominalCodeApiResponse, TaxRateApiResponse, TrackingCategoryApiResponse, ValueApiResponse } from '../../api/EntityApi';
import { NominalCodeType } from '../../api/CalculationApi';
import { RuleApi } from '../../api/RuleApi';
import { SourceApi, SourceApiResponse, SourceType } from '../../api/SourceApi';
import { Autocompletable } from '../../components/form/Autocomplete';
import { DoNotRechargeId, RuleType } from '../../config/Constants';
import { CountryCode } from '../../config/CountryCode';
import { CurrencyCode } from '../../config/CurrencyCode';
import { Rule } from './Rule';
import { Source } from './Source';

export class Value implements Autocompletable {
  id: string = '';
  _name: string = '';
  status: ResourceStatus;

  constructor() {
    makeObservable(this, {
      id: observable,
      name: computed,
    });
  }

  static fromApi(apiData: ValueApiResponse): Value {
    const value = new Value();
    value.id = apiData.externalId;
    value._name = apiData.name;
    value.status = apiData.status;
    return value;
  }

  get name(): string {
    return this._name;
  }
}

export class NominalCode extends Value {
  type: NominalCodeType = NominalCodeType.INCOME;
  externalId: string = '';
  isSystemAccount: boolean = false;

  get name(): string {
    return `${this.id} - ${this._name}`;
  }

  static fromApi(apiData: NominalCodeApiResponse): NominalCode {
    const value = new NominalCode();
    value.id = apiData.code;
    value.externalId = apiData.externalId;
    value._name = apiData.name;
    value.type = apiData.type as NominalCodeType;
    value.status = apiData.status;
    value.isSystemAccount = apiData.systemAccount;
    return value;
  }
}

export class TrackingCategory extends Value {
  type: string = '';
  typeId: string = '';

  get name(): string {
    return `${this.type} - ${this._name}`;
  }

  get option(): string {
    return this._name;
  }

  static fromApi(apiData: TrackingCategoryApiResponse): TrackingCategory {
    const value = new TrackingCategory();
    value.id = apiData.externalId;
    value._name = apiData.name;
    value.type = apiData.type;
    value.typeId = apiData.externalId.split(':')[0];
    value.status = apiData.status;
    return value;
  }
}

export class Contact extends Value {
  isCustomer: boolean = false;
  isSupplier: boolean = false;

  static fromApi(apiData: ContactApiResponse): Contact {
    const value = new Contact();
    value.id = apiData.externalId;
    value._name = apiData.name;
    value.isSupplier = apiData.supplier;
    value.isCustomer = apiData.customer;
    value.status = apiData.status;
    return value;
  }
}

export class TaxRate {
  displayRate: number;
  effectiveRate: number;
  id: number;
  name: string;
  taxType: string;
  canApplyToAssets: boolean;
  canApplyToEquity: boolean;
  canApplyToExpenses: boolean;
  canApplyToLiabilities: boolean;
  canApplyToRevenue: boolean;

  static fromApi(apiData: Partial<TaxRateApiResponse>): TaxRate {
    const value = new TaxRate();
    value.id = apiData.id;
    value.displayRate = apiData.displayRate;
    value.effectiveRate = apiData.effectiveRate;
    value.name = apiData.name;
    value.taxType = apiData.taxType;

    value.canApplyToAssets = apiData.canApplyToAssets;
    value.canApplyToEquity = apiData.canApplyToEquity;
    value.canApplyToExpenses = apiData.canApplyToExpenses;
    value.canApplyToLiabilities = apiData.canApplyToLiabilities;
    value.canApplyToRevenue = apiData.canApplyToRevenue;

    return value;
  }
}

export class Entity {
  id: number = 0;
  groupId: number = 0;
  name: string = '';
  legalName: string = '';
  currency: CurrencyCode = CurrencyCode.GBP;
  country: CountryCode = CountryCode.GB;
  periodEnd: string;
  lockDate: DateTime = DateTime.fromSeconds(0);
  headcount: number = 0;
  shortcode: string = '';

  nominalCodes: NominalCode[] = [];
  trackingCategories: TrackingCategory[] = [];
  trackingCategoriesMap: Map<string, TrackingCategory> = new Map();
  contacts: Contact[] = [];
  contactsMap: Map<string, Contact> = new Map();
  taxRates: TaxRate[] = [];
  rules: Rule[] = [];
  archivedRules: Rule[] = [];
  hasCompletedDataImport: boolean = true;
  hasCompletedTransactionImport: boolean = true;
  currencies: CurrencyCode[] = [];

  sources: Source[] = [];

  connected = true;

  rateLimitExpires = DateTime.now().minus({ day: 1 });

  _costRuleOptions: Map<number, string> = new Map();
  _salesRuleOptions: Map<number, string> = new Map();
  _balanceSheetRuleOptions: Map<number, string> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: EntityApiResponse): Entity {
    const entity = new Entity();
    entity.id = apiData.id;
    entity.name = apiData.name;
    entity.country = CountryCode[apiData.country as keyof typeof CountryCode];
    entity.currency = CurrencyCode[apiData.currency as keyof typeof CurrencyCode];
    entity.headcount = apiData.headcount;
    entity.periodEnd = `${apiData.financialYearEndDay}/${apiData.financialYearEndMonth}`;
    entity.shortcode = apiData.shortCode;
    entity.lockDate = DateTime.fromISO(apiData.lockDate);
    entity.currencies = apiData.currencies ? apiData.currencies.map(code => code as CurrencyCode) : [entity.currency];
    entity.hasCompletedDataImport = apiData.hasCompletedDataImport;
    entity.hasCompletedTransactionImport = apiData.hasCompletedTransactionImport;
    entity.rateLimitExpires = DateTime.fromISO(apiData.rateLimitDateTime);
    return entity;
  }

  toApi = (): Partial<EntityApiResponse> => ({
    headcount: this.headcount,
  });

  loadRules = (): Promise<Entity> => {
    return RuleApi
      .getRules(this.groupId, this.id)
      .then(action(rulesResponse => {
        // Rules API here gives us the latest version of these rules
        this.rules = [];
        rulesResponse.rules.map(ruleApiResponse => {
          const rule = Rule.fromApi(ruleApiResponse, this.groupId);
          this.addRule(rule);
        });

        this.archivedRules = rulesResponse.archivedRules.map(ruleApiResponse => {
          const rule = Rule.fromApi(ruleApiResponse, this.groupId);
          return rule;
        });

        if (this.rules.filter(rule => rule.type === RuleType.NONE).length === 0) {
          this._costRuleOptions.set(DoNotRechargeId, "Don't recharge (expense)");
        }

        if (this.rules.filter(rule => rule.type === RuleType.NONE_SALES).length === 0) {
          this._salesRuleOptions.set(DoNotRechargeId, "Don't recharge (revenue)");
        }

        if (this.rules.filter(rule => rule.type === RuleType.NONE_BALANCE).length === 0) {
          this._balanceSheetRuleOptions.set(DoNotRechargeId, "Don't recharge (balance sheet)");
        }

        return this;
      }));
  };

  setSources = (sourcesResponse: SourceApiResponse[]): void => {
    this.sources = sourcesResponse.map(sourceResponse => Source.fromApi(sourceResponse));
    this.connected = this.sources.map(source => source.connected).reduce((a, b) => a && b, true);
  };

  createSource = (type: SourceType): Promise<void> => {
    return SourceApi
      .createSource(this.groupId, this.id, type)
      .then((source: SourceApiResponse) => {
        this.sources.push(Source.fromApi(source));
      });
  };

  archiveRule = (rule: Rule): Promise<void> => {
    return RuleApi
      .archiveRule(this.groupId, this.id, rule.id)
      .then(() => {
        this.rules = this.rules.filter(r => r.id != rule.id);
        this.archivedRules.push(rule);
      });
  };

  restoreRule = (rule: Rule): Promise<void> => {
    return RuleApi
      .restoreRule(this.groupId, this.id, rule.id)
      .then(() => {
        this.archivedRules = this.archivedRules.filter(r => r.id != rule.id);
        this.addRule(rule);
      });
  };

  addRule = (rule: Rule) => {
    if (!this.rules.find(r => r.ruleApportionmentId === rule.ruleApportionmentId)) {
      this.rules.push(rule);
    }
    
    if (rule.isCostBased) {
      this._costRuleOptions.set(rule.ruleApportionmentId, rule.name);
    } else if (rule.isSalesBased) {
      this._salesRuleOptions.set(rule.ruleApportionmentId, rule.name);
    } else if (rule.isBalanceSheetBased) {
      this._balanceSheetRuleOptions.set(rule.ruleApportionmentId, rule.name);
    }
  };

  loadValues = (): Promise<Entity> => {
    return EntityApi
      .loadValues(this.groupId, this.id)
      .then(action(values => {
        this.nominalCodes = values.nominalCodes
          .filter(v => v.code)
          .map(v => NominalCode.fromApi(v));
        this.trackingCategories = values.trackingCategories.map(v => TrackingCategory.fromApi(v));
        this.trackingCategoriesMap = new Map(this.trackingCategories.map(c => [c.id, c]));
        this.contacts = values.contacts.map(v => Contact.fromApi(v));
        this.contactsMap = new Map(this.contacts.map(c => [c.id, c]));
        this.taxRates = values.taxRates.map(v => TaxRate.fromApi(v));
        return this;
      }));
  };

  save = (): Promise<Entity> => {
    return EntityApi
      .updateEntity(this.groupId, this.id, this.toApi())
      .then(() => this);
  };

  costRuleOptions = (): Map<number, string> => {
    return this._costRuleOptions;
  };

  salesRuleOptions = (): Map<number, string> => {
    return this._salesRuleOptions;
  };

  balanceSheetRuleOptions = (): Map<number, string> => {
    return this._balanceSheetRuleOptions;
  };

  get costRules(): Rule[] {
    return this.rules.filter(rule => rule.isCostBased);
  }

  get salesRules(): Rule[] {
    return this.rules.filter(rule => rule.isSalesBased);
  }

  get balanceSheetRules(): Rule[] {
    return this.rules.filter(rule => rule.isBalanceSheetBased);
  }

  get maydayRules(): Rule[] {
    return this.rules.filter(rule => rule.isMaydayRule);
  }

  get defaultRule(): Rule {
    const defaults = this.costRules.filter(rule => rule.applyToUnmatched);
    return defaults.length > 0 ? defaults[0] : null;
  }

  get defaultIncomeRule(): Rule {
    const defaults = this.salesRules.filter(rule => rule.applyToUnmatched);
    return defaults.length > 0 ? defaults[0] : null;
  }

  get defaultBalanceSheetRule(): Rule {
    const defaults = this.balanceSheetRules.filter(rule => rule.applyToUnmatched);
    return defaults.length > 0 ? defaults[0] : null;
  }

  get hasDefaultRule(): boolean {
    return this.rules.filter(rule => rule.isCostBased && rule.applyToUnmatched).length > 0;
  }

  get hasDefaultIncomeRule(): boolean {
    return this.rules.filter(rule => rule.isSalesBased && rule.applyToUnmatched).length > 0;
  }

  get hasDefaultBalanceSheetRule(): boolean {
    return this.rules.filter(rule => rule.isBalanceSheetBased && rule.applyToUnmatched).length > 0;
  }

  getNominalCode = (id: string): NominalCode => {
    return this.nominalCodes.find(nominalCode => nominalCode.id === id);
  };

  getNominalCodeByExternalId = (id: string): NominalCode => {
    return this.nominalCodes.find(nominalCode => nominalCode.externalId === id);
  };

  getNominalCodeName = (id: string): string => {
    const code = this.getNominalCode(id);
    return code ? code.name : id;
  };

  getNominalCodeNameByExternalId = (id: string): string => {
    return this.nominalCodes.find(nominalCode => nominalCode.externalId === id)?.name;
  };

  getContactName = (id: string): string => {
    return this.contactsMap.get(id)?.name || '';
  };

  // Helpers for cost / sale specific rules
  get costCodes(): NominalCode[] {
    return this.nominalCodes.filter(code => code.status === ResourceStatus.ACTIVE && code.type === NominalCodeType.EXPENSE);
  }

  get costCodesByExternalId(): Autocompletable[] {
    return this.byExternalId(this.costCodes);
  }

  get incomeCodesByExternalId(): Autocompletable[] {
    return this.byExternalId(this.incomeCodes);
  }

  get balanceSheetCodesByExternalId(): Autocompletable[] {
    return this.byExternalId(this.balanceSheetCodes);
  }

  get nominalCodesByExternalId(): Autocompletable[] {
    return this.byExternalId(this.nominalCodes);
  }

  byExternalId = (codes: NominalCode[]): Autocompletable[] => codes
    .map(code => ({
      id: code.externalId,
      name: code.name,
    }));

  get incomeCodes(): NominalCode[] {
    return this.nominalCodes.filter(code => code.status === ResourceStatus.ACTIVE && code.type === NominalCodeType.INCOME);
  }

  get balanceSheetCodes(): NominalCode[] {
    return this.nominalCodes.filter(code => code.status === ResourceStatus.ACTIVE && (code.type === NominalCodeType.ASSET || code.type === NominalCodeType.LIABILITY));
  }

  get nonSystemBalanceSheetCodes(): NominalCode[] {
    return this.balanceSheetCodes.filter(code => !code.isSystemAccount);
  }

  get activeContacts(): Contact[] {
    return this.contacts.filter(contact => contact.status === ResourceStatus.ACTIVE).sort((a, b) => a.name.localeCompare(b.name));
  }

  get activeTrackingCategories(): Value[] {
    return this.trackingCategories.filter(trackingCategory => trackingCategory.status === ResourceStatus.ACTIVE);
  }

  get trackingCategoryTypes(): Autocompletable[] {
    const categories: Map<string, Autocompletable> = new Map(
      this.trackingCategories.map(cat => {
        return [
          cat.typeId,
          {
            name: cat.type,
            id: cat.typeId,
          },
        ];
      })
    );
    return [...categories.values()];
  }

  trackingCategoryOptions = (typeId: string): Autocompletable[] => this.trackingCategories
    .filter(cat => cat.typeId === typeId)
    .map(cat => ({ id: cat.id, name: cat.option }));

  trackingCategoryOptionName = (id: string): string => {
    return this.trackingCategoriesMap.get(id)?.option || '';
  };

  get defaultTaxRate(): TaxRate {
    const noTaxTypes = ['NONE', 'BASEXCLUDED'];
    const noTaxRate = this.taxRates.filter(taxRate => noTaxTypes.includes(taxRate.taxType));

    return noTaxRate.length > 0 ? noTaxRate[0] : this.taxRates[0];
  }

  taxRateOptions = (code: NominalCode): Map<string, string> => {
    if (!code) return new Map();

    return new Map(
      this.taxRates
        .filter(taxRate => {
          switch (code.type) {
            case NominalCodeType.ASSET: return taxRate.canApplyToAssets;
            case NominalCodeType.EQUITY: return taxRate.canApplyToEquity;
            case NominalCodeType.EXPENSE: return taxRate.canApplyToExpenses;
            case NominalCodeType.LIABILITY: return taxRate.canApplyToLiabilities;
            case NominalCodeType.INCOME: return taxRate.canApplyToRevenue;
            default: return false;
          }
        })
        .map(taxRate => [taxRate.taxType, taxRate.name])
    );
  };

  getTaxRate = (taxType: string): TaxRate => {
    return this.taxRates.filter(taxRate => taxRate.taxType === taxType)[0];
  };

  get isRateLimited(): boolean {
    return this.rateLimitExpires.isValid && this.rateLimitExpires > DateTime.now();
  }
}
